import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';

import Copy from 'Src/components/Copy';
import { useFeesAndGratuties } from 'Src/feature/fees-and-gratuities/useFeesAndGratuities';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';

const sectionId = 'OpenFeeModal';

interface OpenFeeModalProps {
  open: boolean;
  feeId: number;
  onSave: (price: number) => void;
  onClose: () => void;
}

interface FormModel {
  value: number;
}

const { Form, Field } = getTypedFormComponents<FormModel>();

const OpenFeeModalContent: FC<OpenFeeModalProps> = ({ feeId, onSave, onClose }) => {
  const { t } = useTranslation();
  const { data } = useFeesAndGratuties.query.getAll();

  const form = useForm<FormModel>({
    defaultValues: {
      value: 0,
    },
    schema: object().shape({
      value: number().label(t('checkFees.labels.editOpenFeeModal.value')).required().moreThan(0),
    }),
  });

  const fee = data?.find((fee) => fee.id === feeId);
  const feeName = fee?.posName ? fee.posName : fee?.name;

  const onConfirm = async () => {
    const { isValid, values } = await form.action.submit();
    if (!isValid) return;
    // FYI form doesn't return a number... it's a string. So do a parse.
    onSave(parseFloat(values.value.toString())); // holy fugly batman
  };

  return (
    <>
      <ModalHeader>
        <ModalTitle copy={fee?.isGratuity ? t('checkFees.labels.editOpenFeeModal.titleGratuity') : t('checkFees.labels.editOpenFeeModal.titleFee')} />
        <Copy size="lg" weight="semi-bold" value={feeName} />
      </ModalHeader>
      <ModalContent>
        <Form usedForm={form}>
          <Field
            type={fee?.type === 'OpenAmount' ? 'currencyInput' : 'percentInput'}
            name="value"
          />
        </Form>
      </ModalContent>
      <ModalActions>
        <ModalAction
          label={t('labels.cancel')}
          onClick={onClose}
          color="secondary"
        />
        <ModalAction
          label={fee?.isGratuity ? t('checkFees.labels.editOpenFeeModal.confirmGratuity') : t('checkFees.labels.editOpenFeeModal.confirmFee')}
          onClick={onConfirm}
          color="primary"
          disabled={!form.state.isValid}
        />
      </ModalActions>
    </>
  );
};

// eslint-disable-next-line react/no-multi-comp
const OpenFeeModal: FC<OpenFeeModalProps> = ({ open, feeId, onSave, onClose }) => (
  <Modal data-testid={sectionId} open={open} variant="grow">
    <OpenFeeModalContent open={open} feeId={feeId} onSave={onSave} onClose={onClose} />
  </Modal>
);

export { OpenFeeModal };
